import { useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { usePageAccessGuard } from "@/hooks/usePageAccessGuard";
import { useSimplifiedUserData } from "@/providers/SimplifiedUserDataProvider";
import { useDocumentDataContext } from "@/providers/DocumentDataProvider";
import { UnifiedItemsContainer } from "@/components/layout/page-containers";
import { PageHandlers, TransformContext } from "@/components/layout/page-containers/UnifiedItemsContainer/types";
import { documentsConfig } from "./unified-items/configs/documentsConfig";
import { CardData } from "@/components/layout/element-layout/grid-layout/GridLayout";

// Import stores and types
import {
  DocumentItem,
  documentStore,
  
} from "@/lib/stores/documentStore";
import { documentsDataSource } from "@/lib/dataSources/sources/documents";
import { Version } from "@/lib/stores/storeTypes";
import GlossaryWidget from "@/components/cards/widgets/ObjectsWidget";

/**
 * DocumentsPage - SOURCE OF TRUTH for document operations
 * 
 * This page owns all document-specific:
 * - Store operations (write/update/delete in documentStore)
 * - CRUD handlers (create, update, delete documents)
 * - Card transformation (raw DocumentItem → CardData)
 * - Wizard callbacks (what happens after create/update)
 * 
 * When the structure of documents changes, update THIS file.
 */
export default function DocumentsPage() {
  usePageAccessGuard('documents');
  
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedGame } = useSimplifiedUserData();
  const { createDocument, updateDocument, deleteDocument, loadData: refreshDocuments } = useDocumentDataContext();

  // #region Store Operations
  /**
   * Document-specific store operations
   */
  const storeOperations = useMemo(() => ({
    writeToStore: (items: DocumentItem[]) => {
      documentStore.setData(items);
    },
    updateInStore: (id: string, data: Partial<DocumentItem>) => {
      const current = documentStore.getData();
      const updated = current.map(item => 
        item.id === id ? { ...item, ...data } : item
      );
      documentStore.setData(updated);
    },
    deleteFromStore: (id: string) => {
      const current = documentStore.getData();
      documentStore.setData(current.filter(item => item.id !== id));
    },
  }), []);
  // #endregion

  // #region CRUD Handlers
  /**
   * Document-specific CRUD handlers
   */
  const handlers = useMemo(() => ({
    onCreate: async (data: any, folderId?: string): Promise<DocumentItem> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const documentData = {
        ...data,
        game_id: data.game_id || selectedGame.id,
        folder: folderId || data.folder || null,
      };

      const createdDocument = await createDocument(documentData);
      return createdDocument;
    },

    onUpdate: async (id: string, data: Partial<DocumentItem>): Promise<DocumentItem> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      const existingDocument = documentStore.getData().find(item => item.id === id);
      if (!existingDocument) {
        throw new Error("Document not found");
      }

      const documentData = {
        ...existingDocument,
        ...data,
        id,
        game_id: data.game_id || selectedGame.id,
      };

      const updatedDocument = await updateDocument(documentData);
      return updatedDocument;
    },

    onDelete: async (id: string): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      await deleteDocument(id, selectedGame.id);
      storeOperations.deleteFromStore(id);
    },

    onBulkDelete: async (ids: string[]): Promise<void> => {
      if (!selectedGame?.id) {
        throw new Error("No game selected");
      }

      for (const id of ids) {
        await deleteDocument(id, selectedGame.id);
        storeOperations.deleteFromStore(id);
      }
    },
  }), [selectedGame, createDocument, updateDocument, deleteDocument, storeOperations]);
  // #endregion

  // #region Card Transformation
  /**
   * Transform document items to CardData for display
   */
  const transformToCard = useCallback((item: DocumentItem, context: TransformContext): CardData => {
    const cards = documentsDataSource.transformers.toCard?.([item], {
      tags: context.tags,
      getUserNameById: context.getUserNameById,
    }) || [];
    
    return cards[0] || {
      id: item.id,
      title: item.name,
      gameId: item.game_id,
      folder: item.folder,
      tags: [],
    };
  }, []);

  /**
   * Render a document card
   */
  const renderCard = useCallback((card: CardData, tags: any[]) => {
    return (
      <GlossaryWidget
        id={card.id}
        gameId={card.gameId}
        title={card.title}
        owner={card.owner}
        tags={card.tags}
        description={card.description}
        lastUpdate={card.lastUpdate}
        version={card.version as number | Version}
        status={card.status}
        folder={card.folder as string}
        itemType="documents"
        icon={card.icon}
        iconBg={card.iconBg}
        shadowType={card.shadowType}
        shadowColor={card.shadowColor}
        hasVersionMismatch={card.hasVersionMismatch}
        onDelete={card.onDelete}
        onEdit={card.onEdit}
      />
    );
  }, []);
  // #endregion

  // #region Edit Handler
  /**
   * Handle edit action for documents
   */
  const onEdit = useCallback((id: string, data: DocumentItem[], navigateFn: (path: string) => void) => {
    const document = data.find(item => item.id === id);
    navigate(`/documents/${id}`, { state: { itemName: document?.name } });
  }, [navigate]);
  // #endregion

  // #region Wizard Callbacks
  /**
   * Document-specific wizard callbacks
   */
  const wizardCallbacks = useMemo(() => ({
    onItemCreated: async (item: DocumentItem) => {
      toast({
        title: "Success",
        description: `Document "${item.name}" created successfully`,
      });
      
      if (selectedGame?.id) {
        try {
          await refreshDocuments();
        } catch (error) {
          console.warn("Failed to refresh documents after creation:", error);
        }
      }
    },

    onItemUpdated: async (item: DocumentItem) => {
      toast({
        title: "Success",
        description: `Document "${item.name}" updated successfully`,
      });
      
      if (selectedGame?.id) {
        try {
          await refreshDocuments();
        } catch (error) {
          console.warn("Failed to refresh documents after update:", error);
        }
      }
    },

    onWizardClose: () => {},
  }), [toast, selectedGame, refreshDocuments]);
  // #endregion

  // #region Combined PageHandlers
  const pageHandlers: PageHandlers<DocumentItem> = useMemo(() => ({
    storeOperations,
    handlers,
    transformToCard,
    renderCard,
    wizardCallbacks,
    onEdit,
    features: {
      supportsPendingChanges: true,
      supportsBulkCreate: false,
      supportsFlowView: false,
    },
  }), [storeOperations, handlers, transformToCard, renderCard, wizardCallbacks, onEdit]);
  // #endregion

  return (
    <UnifiedItemsContainer 
      itemType="documents"
      handlers={pageHandlers}
      config={documentsConfig}
    />
  );
}
